import type { CSSProperties } from 'react';

// =============================================================================
// STYLES - Mobile-first, touch-friendly
// =============================================================================
export const styles: Record<string, CSSProperties> = {
  // Container
  container: {
    maxWidth: '600px',
    margin: '0 auto',
    fontFamily: 'inherit',
    padding: '0 0.5rem',
  },

  // Header
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
    paddingBottom: '0.75rem',
    borderBottom: '1px solid var(--color-border)',
  },
  title: {
    margin: 0,
    fontSize: 'var(--text-2xl)',
    fontWeight: 600,
  },

  // Messages
  errorBox: {
    background: 'var(--color-error-bg)',
    border: '1px solid #fecaca',
    color: '#991b1b',
    padding: '0.75rem 1rem',
    borderRadius: 'var(--radius-md)',
    marginBottom: '1rem',
    fontSize: 'var(--text-base)',
  },
  successBox: {
    background: 'var(--color-success-bg)',
    border: '1px solid #bbf7d0',
    color: 'var(--color-success-text)',
    padding: '0.75rem 1rem',
    borderRadius: 'var(--radius-md)',
    marginBottom: '1rem',
    fontSize: 'var(--text-base)',
  },

  // Search
  searchSection: {
    background: 'var(--color-bg)',
    padding: '1rem',
    borderRadius: 'var(--radius)',
    marginBottom: '0.75rem',
    position: 'relative',
  },
  searchingText: {
    fontSize: 'var(--text-sm)',
    color: 'var(--color-text-light)',
    marginTop: '0.25rem',
  },
  searchResults: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: '100%',
    marginTop: '4px',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-md)',
    background: 'white',
    maxHeight: '200px',
    overflowY: 'auto',
    zIndex: 10,
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  },
  searchResult: {
    padding: '0.75rem 1rem',
    borderBottom: '1px solid var(--color-border)',
    cursor: 'pointer',
  },
  resultName: {
    fontWeight: 500,
    fontSize: 'var(--text-lg)',
  },
  resultEmail: {
    fontSize: 'var(--text-md)',
    color: 'var(--color-text-light)',
  },
  resultCompany: {
    fontSize: 'var(--text-sm)',
    color: 'var(--color-text-light)',
    fontStyle: 'italic',
  },
  noResults: {
    fontSize: 'var(--text-sm)',
    color: 'var(--color-text-light)',
    marginTop: '0.5rem',
    fontStyle: 'italic',
  },

  // Selected banner
  selectedBanner: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.75rem 1rem',
    background: '#dbeafe',
    borderRadius: 'var(--radius-md)',
    marginBottom: '1rem',
    fontSize: 'var(--text-base)',
    color: '#1e40af',
  },
  clearBtn: {
    background: 'none',
    border: 'none',
    color: '#1e40af',
    textDecoration: 'underline',
    cursor: 'pointer',
    fontSize: 'var(--text-md)',
  },

  // Duplicate warning
  duplicateWarning: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '1rem',
    padding: '0.75rem 1rem',
    background: '#fef3c7',
    borderRadius: 'var(--radius-md)',
    marginBottom: '1rem',
    fontSize: 'var(--text-base)',
    color: '#92400e',
  },
  loadBtn: {
    background: '#92400e',
    color: 'white',
    border: 'none',
    padding: '0.375rem 0.75rem',
    borderRadius: 'var(--radius-sm)',
    cursor: 'pointer',
    fontSize: 'var(--text-md)',
    fontWeight: 500,
    // The message beside it is a sentence, so without these the button gets
    // squeezed and its own label wraps onto two lines.
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },

  // Sections
  section: {
    background: 'white',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius)',
    marginBottom: '0.5rem',
    overflow: 'hidden',
  },
  sectionContent: {
    padding: '0.75rem 1rem',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.75rem 1rem',
    background: 'var(--color-bg)',
    borderBottom: '1px solid var(--color-border)',
  },
  sectionTitle: {
    fontSize: 'var(--text-md)',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.025em',
    color: 'var(--color-text)',
  },
  required: {
    fontSize: 'var(--text-xs)',
    color: 'var(--color-text-light)',
  },

  // Collapsible header
  collapsibleHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.75rem 1rem',
    background: 'var(--color-bg)',
    cursor: 'pointer',
    userSelect: 'none',
  },
  expandHint: {
    fontSize: 'var(--text-xs)',
    color: 'var(--color-text-light)',
  },
  collapsibleContent: {
    padding: '0.75rem 1rem',
    borderTop: '1px solid var(--color-border)',
  },

  // Form elements
  formGroup: {
    marginBottom: '0.5rem',
    flex: 1,
  },
  label: {
    display: 'block',
    fontSize: 'var(--text-md)',
    fontWeight: 500,
    marginBottom: '0.375rem',
    color: 'var(--color-text)',
  },
  labelHint: {
    fontWeight: 400,
    color: 'var(--color-text-light)',
    fontSize: 'var(--text-sm)',
  },
  input: {
    width: '100%',
    padding: '0.625rem 0.75rem',
    fontSize: 'var(--text-xl)', // Prevents iOS zoom
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-md)',
    boxSizing: 'border-box',
    minHeight: '44px', // Touch-friendly
  },
  inputWarning: {
    borderColor: '#f59e0b',
    background: 'var(--color-warning-bg)',
  },
  helpText: {
    fontSize: 'var(--text-xs)',
    color: 'var(--color-text-light)',
    marginTop: '0.25rem',
  },
  row: {
    display: 'flex',
    gap: '0.75rem',
  },

  // Toggle buttons
  toggleContainer: {
    display: 'flex',
    background: 'var(--color-bg)',
    borderRadius: 'var(--radius-md)',
    padding: '4px',
    marginBottom: '1rem',
  },
  toggleBtn: {
    flex: 1,
    padding: '0.625rem 1rem',
    border: 'none',
    background: 'transparent',
    borderRadius: 'var(--radius-sm)',
    cursor: 'pointer',
    fontSize: 'var(--text-base)',
    fontWeight: 500,
    color: 'var(--color-text-light)',
    transition: 'all 0.2s',
    minHeight: '44px',
  },
  toggleBtnActive: {
    background: 'white',
    color: 'var(--color-primary)',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },

  // Checkbox
  checkboxRow: {
    marginBottom: '0.75rem',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    cursor: 'pointer',
    fontSize: 'var(--text-base)',
    minHeight: '44px',
  },

  // Address
  addressLabel: {
    fontSize: 'var(--text-sm)',
    fontWeight: 600,
    textTransform: 'uppercase',
    color: 'var(--color-text-light)',
    marginBottom: '0.5rem',
  },

  // Footer
  footer: {
    marginTop: '0.75rem',
    paddingTop: '0.75rem',
    borderTop: '1px solid var(--color-border)',
    position: 'sticky',
    bottom: 0,
    background: 'white',
    marginLeft: '-1rem',
    marginRight: '-1rem',
    paddingLeft: '1rem',
    paddingRight: '1rem',
    paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))',
  },
  footerNote: {
    fontSize: 'var(--text-sm)',
    color: 'var(--color-text-light)',
    marginBottom: '0.75rem',
    textAlign: 'center',
  },
  footerButtons: {
    display: 'flex',
    gap: '0.75rem',
  },
  cancelBtn: {
    flex: 1,
    padding: '0.75rem 1rem',
    fontSize: 'var(--text-xl)',
    fontWeight: 500,
    color: 'var(--color-text)',
    background: 'white',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    minHeight: '48px',
  },
  saveBtn: {
    flex: 2,
    padding: '0.75rem 1rem',
    fontSize: 'var(--text-xl)',
    fontWeight: 600,
    color: 'white',
    background: 'var(--color-primary)',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    minHeight: '48px',
  },

  // Modal wrapper
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    zIndex: 1000,
    padding: 0,
  },
  modal: {
    background: 'white',
    borderRadius: '16px 16px 0 0',
    width: '100%',
    maxWidth: '600px',
    maxHeight: '90vh',
    overflowY: 'auto',
    padding: '1.5rem',
    paddingBottom: 'calc(1.5rem + env(safe-area-inset-bottom))',
  },

  // Peace of Sleep styles
  posSubsection: {
    marginBottom: '1.25rem',
    paddingBottom: '1rem',
    borderBottom: '1px solid var(--color-border)',
  },
  posSubsectionTitle: {
    fontSize: 'var(--text-sm)',
    fontWeight: 600,
    textTransform: 'uppercase',
    color: 'var(--color-text-light)',
    marginBottom: '0.75rem',
    letterSpacing: '0.025em',
  },
  propertiesList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  propertyItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px',
    background: 'var(--color-bg)',
    borderRadius: 'var(--radius)',
    border: '1px solid var(--color-border)',
  },
  propertyInfo: {
    flex: 1,
  },
  propertyName: {
    fontWeight: 600,
    fontSize: 'var(--text-lg)',
    marginBottom: '4px',
  },
  propertyMeta: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    fontSize: 'var(--text-sm)',
    color: 'var(--color-text-light)',
  },
  tierBadge: {
    padding: '2px 8px',
    borderRadius: 'var(--radius-lg)',
    fontSize: 'var(--text-xs)',
    fontWeight: 600,
  },
  tierAssured: {
    background: 'var(--color-info-bg)',
    color: 'var(--color-info-text)',
  },
  tierDistinguished: {
    background: '#fef3c7',
    color: '#92400e',
  },
  tierSignature: {
    background: 'linear-gradient(135deg, #fdf4ff, #fce7f3)',
    color: '#86198f',
    border: '1px solid #f0abfc',
  },
  loadingText: {
    fontSize: 'var(--text-md)',
    color: 'var(--color-text-light)',
    fontStyle: 'italic',
    padding: '12px',
  },
  noPropertiesText: {
    fontSize: 'var(--text-md)',
    color: 'var(--color-text-light)',
    padding: '12px',
    background: 'var(--color-bg)',
    borderRadius: 'var(--radius-md)',
    textAlign: 'center',
  },
  manageLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '8px 12px',
    fontSize: 'var(--text-md)',
    fontWeight: 500,
    color: 'var(--color-primary)',
    background: 'transparent',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-md)',
    textDecoration: 'none',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
};
